import { ReactNode } from "react";
import { useAtomValue } from "jotai";

import {
  AssetType,
  isDraftNotTemplateNodeAtomFamily,
} from "@zapier/canvas-state";
import { ChatMock } from "./previews/ChatMock";
import { InterfaceMock } from "./previews/InterfaceMock";
import { TableMock } from "./previews/TableMock";
import { ZapMock } from "./previews/ZapMock";
import { AgentThumbnail } from "./previews/AgentThumbnail";
import {
  UnlinkedChatbot,
  UnlinkedInterface,
  UnlinkedTable,
  UnlinkedZap,
} from "./previews/Unlinked";
import { AssetPreviewWrapper } from "./AssetPreviewWrapper";
import styles from "./AssetPreview.module.css";

type Props = {
  assetType: AssetType;
  id: string;
  assetId?: string | undefined | null;
  showAssetDetailsButton?: boolean;
  projectId?: string | undefined;
  isAssetNotFound?: boolean;
};

type AssetWrapperProps = {
  children: ReactNode;
};

function Wrapper({ children }: AssetWrapperProps) {
  return <div className={styles.root}>{children}</div>;
}

type GetGhostNodeChildrenParams = Pick<
  Params,
  "id" | "assetType" | "assetId"
> & {
  isDraftNotTemplateNode: boolean;
};

/* Agent nodes don't have a mock SVG since we show the step title instead */
function getGhostNodeChildren({
  id,
  assetType,
  isDraftNotTemplateNode,
  assetId,
}: GetGhostNodeChildrenParams) {
  switch (assetType) {
    case "table":
      return (
        <Wrapper>
          <UnlinkedTable />
        </Wrapper>
      );
    case "zap":
      return (
        <Wrapper>
          <UnlinkedZap />
        </Wrapper>
      );
    case "interface":
      return (
        <Wrapper>
          <UnlinkedInterface />
        </Wrapper>
      );
    case "chatbot":
      return (
        <Wrapper>
          <UnlinkedChatbot />
        </Wrapper>
      );
    case "agent":
      if (isDraftNotTemplateNode && assetId) {
        return <AgentThumbnail id={id} agentId={assetId} />;
      }

      /**
       * Return null for agent nodes without assetId or when asset not found
       * (step title will be shown instead - see AssetNodeSkeleton/AssetNotFound)
       */
      return null;

    default:
      return null;
  }
}

type Params = {
  id: Props["id"];
  assetType: Props["assetType"];
  assetId: string | undefined | null;
  isDraftNotTemplateNode: boolean;
  isAssetNotFound: boolean;
};

function getChildren({
  id,
  assetType,
  assetId,
  isDraftNotTemplateNode,
  isAssetNotFound,
}: Params) {
  if (!assetId || isDraftNotTemplateNode || isAssetNotFound) {
    return getGhostNodeChildren({
      id,
      assetType,
      isDraftNotTemplateNode,
      assetId,
    });
  }

  switch (assetType) {
    case "table":
      return (
        <Wrapper>
          <TableMock />
        </Wrapper>
      );
    case "zap":
      return (
        <Wrapper>
          <ZapMock />
        </Wrapper>
      );
    case "interface":
      return (
        <Wrapper>
          <InterfaceMock />
        </Wrapper>
      );
    case "chatbot":
      return (
        <Wrapper>
          <ChatMock />
        </Wrapper>
      );
    case "agent":
      return <AgentThumbnail id={id} agentId={assetId!} />;

    default:
      return null;
  }
}

export const AssetPreview = ({
  id,
  assetType,
  assetId,
  showAssetDetailsButton = false,
  projectId,
  isAssetNotFound = false,
}: Props) => {
  const isDraftNotTemplateNode = useAtomValue(
    isDraftNotTemplateNodeAtomFamily(id),
  );

  return (
    <AssetPreviewWrapper
      id={id}
      assetType={assetType}
      assetId={assetId}
      showAssetDetailsButton={showAssetDetailsButton}
      projectId={projectId}
    >
      {getChildren({
        id,
        assetType,
        assetId,
        isDraftNotTemplateNode,
        isAssetNotFound,
      })}
    </AssetPreviewWrapper>
  );
};
